<template>
    <ChatWidgetSection :title="t('chat.widgets.notesPanel.aboutYou')">
        <template v-if="!hasAboutYouWidgets" #description>{{ t("chat.widgets.notesPanel.aboutYouEmpty") }}</template>
        <template #content>
            <ChatWidgetAccordion v-model="aboutYouAccordionExpandedItems" :accordion-ids="aboutYouAccordionIds">
                <template #[makeTriggerSlot(AccordionId.INSIGHTS)]>
                    <ChatWidgetTitle :show-status="insights.length > 0" class="pl-2">
                        <template #text>
                            {{ t("chat.widgets.notesPanel.insightCount", { count: insights.length }, insights.length) }}
                        </template>
                    </ChatWidgetTitle>
                </template>
                <template #[makeContentSlot(AccordionId.INSIGHTS)]>
                    <div class="fs-mask flex flex-col gap-3 p-1">
                        <InsightWidget v-for="insight in insights" :key="`${insight.message.chat_message_id}-${insight.lineIdx}`" :data="insight.line" class="px-3" />
                    </div>
                </template>
                <template #[makeTriggerSlot(AccordionId.MEMORIES)]>
                    <ChatWidgetTitle :show-status="(inferences?.length ?? 0) > 0" class="pl-2">
                        <template #text>
                            {{ t("chat.widgets.notesPanel.noteCount", { count: inferences?.length ?? 0 }, inferences?.length ?? 0) }}
                        </template>
                    </ChatWidgetTitle>
                </template>
                <template #[makeContentSlot(AccordionId.MEMORIES)]>
                    <div class="px-4 fs-mask">
                        <ChatActionProfileInferences v-if="(inferences?.length ?? 0) > 0" :inferences="inferences!" @forget-inference="forgetInference" />
                    </div>
                </template>
                <template #[makeTriggerSlot(AccordionId.CUSTOMIZABLE_TEXT)]>
                    <ChatWidgetTitle :show-status="!!customizableText" class="pl-2">
                        <template #text>
                            <span :title="customizableText?.line.action_params.title ?? undefined">{{
                                truncate(customizableText?.line.action_params.title ?? "", { length: 100, omission: "..." })
                            }}</span>
                        </template>
                    </ChatWidgetTitle>
                </template>
                <template #[makeContentSlot(AccordionId.CUSTOMIZABLE_TEXT)]>
                    <div class="px-4 fs-mask">
                        <ChatActionCustomizableText
                            v-if="customizableText"
                            :data="customizableText?.line"
                            :message-id="customizableText?.message.id"
                            :line-idx="customizableText.lineIdx" />
                    </div>
                </template>
            </ChatWidgetAccordion>
        </template>
    </ChatWidgetSection>
    <HorizontalDivider />
    <ChatWidgetSection :title="t('chat.widgets.notesPanel.nextSteps')" class="pb-10">
        <template v-if="!hasNextStepsWidgets" #description>{{ t("chat.widgets.notesPanel.nextStepsEmpty") }}</template>
        <template #content>
            <ChatWidgetAccordion v-model="nextStepsAccordionExpandedItems" :accordion-ids="nextStepsAccordionIds">
                <template #[makeTriggerSlot(AccordionId.ACTION_ITEMS)]>
                    <ChatWidgetTitle :show-status="actionItems.length > 0">
                        <template #icon>
                            <div class="h-8 w-8">
                                <ProgressCircle v-bind="actionItemsProgress" :aria-label="t('chat.widgets.notesPanel.actionItemsProgress')" />
                            </div>
                        </template>
                        <template #text>{{ t("chat.widgets.notesPanel.actionItems") }}</template>
                    </ChatWidgetTitle>
                </template>
                <template v-if="actionItems.length > 0" #[makeContentSlot(AccordionId.ACTION_ITEMS)]>
                    <div
                        :ref="
                            (element) =>
                                registerFocusableAction({
                                    messageId: actionItems[0].message.chat_message_id,
                                    actionName: actionItems[0].line.action_name,
                                    lineIdx: actionItems[0].lineIdx,
                                    onFocus: () => focusWidget(element, nextStepsAccordionExpandedItems, AccordionId.ACTION_ITEMS),
                                })
                        "
                        class="px-4 flex flex-col gap-4 fs-mask">
                        <ChatActionActionItems
                            v-for="actionItem in actionItems"
                            :key="`${actionItem.message.chat_message_id}-${actionItem.lineIdx}`"
                            :data="actionItem.line"
                            :message-id="actionItem.message.chat_message_id"
                            :line-idx="actionItem.lineIdx" />
                    </div>
                </template>

                <template #[makeTriggerSlot(AccordionId.FOLLOW_UP)]>
                    <ChatWidgetTitle :show-status="true" :status-color="followUpWidgetDetails.statusColor">
                        <template #icon>
                            <Calendar class="h-5 w-auto stroke-ink" />
                        </template>
                        <template #text
                            ><span :class="followUpWidgetDetails.textColor">{{ followUpWidgetDetails.text }}</span></template
                        >
                    </ChatWidgetTitle>
                </template>

                <template v-if="!!followUp" #[makeContentSlot(AccordionId.FOLLOW_UP)]>
                    <div class="px-4">
                        <ChatActionFollowupInSidebar :data="followUp.line" :message-id="followUp.message.chat_message_id" :line-idx="followUp.lineIdx" />
                        <div v-if="!!followUpWidgetDetails.subtext" class="mt-3 body text-ink-muted">{{ followUpWidgetDetails.subtext }}</div>
                    </div>
                </template>
            </ChatWidgetAccordion>
        </template>
    </ChatWidgetSection>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import ChatActionActionItems from "~vue/ChatActionActionItems.vue";
import ChatActionCustomizableText from "~vue/ChatActionCustomizableText.vue";
import ChatActionFollowupInSidebar from "~vue/ChatActionFollowupInSidebar.vue";
import ChatActionProfileInferences from "~vue/ChatActionProfileInferences.vue";
import { ChatAction, pluckAction, pluckMultipleActions } from "~vue/chatActions";
import ChatWidgetSection from "~vue/ChatWidgets/ChatWidgetSection.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import ProgressCircle from "~vue/components/ProgressCircle.vue";
import Calendar from "~vue/icons/Calendar.vue";
import type { ChatMessage, FocusableActionParams, UpdateActionState } from "~vue/types/chat";
import type { UserProfileData } from "~vue/types/profile";
import { invariant } from "~vue/utils";
import { formatDate } from "~vue/utils/formatDate";
import truncate from "lodash/truncate";
import { DateTime } from "luxon";
import { type ComponentPublicInstance, computed, inject, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import ChatWidgetAccordion, { makeContentSlot, makeTriggerSlot } from "./ChatWidgetAccordion.vue";
import ChatWidgetTitle from "./ChatWidgetTitle.vue";
import InsightWidget from "./InsightWidget.vue";

enum AccordionId {
    MEMORIES = "memories",
    INSIGHTS = "insights",
    ACTION_ITEMS = "action_items",
    FOLLOW_UP = "follow_up",
    CUSTOMIZABLE_TEXT = "customizable-text",
}

type ChatNadiaNotesProps = {
    messages: ChatMessage[];
    registerFocusableAction?: (action: FocusableActionParams) => void;
};

const { messages, registerFocusableAction = () => {} } = defineProps<ChatNadiaNotesProps>();
const { t } = useI18n();

const coachingSessionId = inject<string>("coachingSessionId");
const updateActionState = inject<UpdateActionState>("updateActionState");
invariant(!!updateActionState, "updateActionState injection is required");

// this is the inference count from messages
const inferenceCount = computed(() => pluckMultipleActions(messages, [ChatAction.INFERRED_PROFILE_ANSWER]).length);
watch(inferenceCount, (newCount, oldCount) => {
    if (newCount > oldCount && !isFetchingInference.value) {
        getProfileInferences();
        openAccordionItem(aboutYouAccordionExpandedItems.value, AccordionId.MEMORIES);
    }
});

const nextStepsAccordionExpandedItems = ref([AccordionId.FOLLOW_UP]);
const aboutYouAccordionExpandedItems = ref([AccordionId.INSIGHTS]);

const deleteInferenceId = ref<number | null>(null);
const deleteUrl = computed(() => `/accounts/profile-data/${deleteInferenceId.value}`);
const { execute: deleteInference, onFetchResponse: onDeleteResponse } = useFetch(deleteUrl, { immediate: false }).delete();

function forgetInference({ id: inferenceId }: { id: number }) {
    deleteInferenceId.value = inferenceId;
    const inferredProfileAnswerActions = pluckMultipleActions(messages, [ChatAction.INFERRED_PROFILE_ANSWER]);
    const data = inferredProfileAnswerActions.find(
        (item) => item.line.type === "action" && item.line.action_name === ChatAction.INFERRED_PROFILE_ANSWER && +item.line.action_params.user_profile_data_id === inferenceId,
    );
    if (!data) {
        invariant(false, `Could not find inferred profile answer action for inference ID ${inferenceId}`);
        return;
    }

    // optimistic UI update
    updateActionState?.({
        action_state: { ...data.line.action_state!, dismissed: true },
        action_params: data.line.action_params,
        lineIdx: data.lineIdx,
        message_id: data.message.chat_message_id,
    });
    if (inferences.value) {
        // optimistic update for inference
        inferences.value = inferences.value.filter((item) => item.id !== deleteInferenceId.value);
        deleteInference();
    }
}

onDeleteResponse((response) => {
    if (response.status === 204) {
        deleteInferenceId.value = null;
    }
});

const {
    isFetching: isFetchingInference,
    data: inferences,
    execute: getProfileInferences,
} = useFetch(`/accounts/profile-data/cs/${coachingSessionId}/`, { immediate: false, initialData: [] }).get().json<UserProfileData[]>();

const insights = computed(() => pluckMultipleActions(messages, [ChatAction.INTERNAL_THINKING, ChatAction.VALUES_INSIGHT]));

const actionItems = computed(() => pluckMultipleActions(messages, [ChatAction.ACTION_ITEMS]));

const followUp = computed(() => pluckAction(messages, ChatAction.FOLLOW_UP));

const customizableText = computed(() => pluckAction(messages, ChatAction.CUSTOMIZABLE_TEXT));

watch(
    [insights, actionItems, followUp, customizableText],
    ([newInsights, newActionItems], [oldInsights, oldActionItems]) => {
        if (newInsights?.length > (oldInsights?.length ?? 0)) {
            openAccordionItem(aboutYouAccordionExpandedItems.value, AccordionId.INSIGHTS);
        }
        if (newActionItems?.length > (oldActionItems?.length ?? 0)) {
            openAccordionItem(nextStepsAccordionExpandedItems.value, AccordionId.ACTION_ITEMS);
        }
        if (followUp.value) {
            openAccordionItem(nextStepsAccordionExpandedItems.value, AccordionId.FOLLOW_UP);
        }
        if (customizableText.value) {
            openAccordionItem(aboutYouAccordionExpandedItems.value, AccordionId.CUSTOMIZABLE_TEXT);
        }
    },
    { immediate: true },
);

const followUpWidgetDetails = computed(() => {
    if (!followUp.value || !followUp.value.line.action_state?.submitted) {
        return {
            text: t("chat.widgets.notesPanel.scheduleNextSession"),
            textColor: "",
            statusColor: undefined,
            subtext: null,
        };
    }

    let subtext = null;

    const { event_at_guessed, event_at_confirmed } = followUp.value.line.action_params;

    if (event_at_confirmed || event_at_guessed) {
        let followUpDate: DateTime = DateTime.now();
        if (event_at_confirmed) {
            followUpDate = DateTime.fromISO(event_at_confirmed);
        } else if (event_at_guessed) {
            followUpDate = DateTime.fromISO(event_at_guessed);
        }

        // Only show the subtext if the follow-up has not elapsed yet
        if (followUpDate.diffNow().as("milliseconds") > 0) {
            const day = formatDate(followUpDate, "weekday");
            const time = formatDate(followUpDate, "time");
            subtext = t("chat.widgets.notesPanel.sessionScheduledDetails", { day, time });
        }
    }

    return {
        text: t("chat.widgets.notesPanel.sessionScheduled"),
        textColor: "text-ink-muted",
        statusColor: "bg-status-success",
        subtext,
    };
});

const actionItemsProgress = computed(() => {
    if (actionItems.value?.length > 0) {
        // Collect all action items from all entries
        let totalItems = 0;
        let totalChecked = 0;

        // Iterate through all action item entries
        actionItems.value.forEach((actionItem) => {
            const { action_items: items } = actionItem.line.action_params;
            if (items && items.length > 0) {
                totalItems += items.length;
                totalChecked += items.filter(({ checked }) => checked).length;
            }
        });

        if (totalItems > 0) {
            const progress = totalChecked === 0 ? 0 : Math.floor((totalChecked / totalItems) * 100);

            return {
                progress,
                content: `${totalChecked}/${totalItems}`,
            };
        }
    }

    return {
        progress: 0,
        content: "0/0",
        gradientFrom: "var(--color-progress-track)",
        gradientTo: "var(--color-progress-track)",
    };
});

const hasAboutYouWidgets = computed(() => {
    if (insights.value.length > 0) {
        return true;
    }

    return (inferences.value?.length ?? 0) > 0;
});

const nextStepsAccordionIds = computed(() => {
    /*
     * "Action Items" is always shown, whereas follow-up
     * is only shown if the follow-up action exists.
     */
    const ids = [AccordionId.ACTION_ITEMS];

    if (followUp.value) {
        ids.push(AccordionId.FOLLOW_UP);
    }

    return ids;
});

const aboutYouAccordionIds = computed(() => {
    /*
     * "Insights" is always shown, whereas "Memories"
     * is only shown if at least one inference exists.
     */
    const ids = [AccordionId.INSIGHTS];

    if ((inferences.value?.length ?? 0) > 0) {
        ids.push(AccordionId.MEMORIES);
    }

    if (customizableText.value) {
        ids.push(AccordionId.CUSTOMIZABLE_TEXT);
    }

    return ids;
});

const hasNextStepsWidgets = computed(() => !!followUp.value || actionItems.value.length > 0);

function openAccordionItem(accordion: AccordionId[], itemId: AccordionId) {
    if (!accordion.includes(itemId)) {
        accordion.push(itemId);
    }
}

function focusWidget(element: Element | ComponentPublicInstance | null, accordion: AccordionId[], itemId: AccordionId) {
    openAccordionItem(accordion, itemId);
    /*
     * We use nextTick so that the effect of `openAccordionItem` is
     * settled into the DOM before we interact with it. The timeout
     * is to add a small delay in between opening an accordion and
     * scrolling to the child element.
     */
    nextTick(() => {
        if (element instanceof HTMLElement) {
            setTimeout(() => element.scrollIntoView({ behavior: "smooth" }), 200);
        }
    });
}
</script>
